import React from 'react';
import { get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Layout, { LayoutContent } from '../../../components/Layout';
import useStyles from './styles';
import route, { artistsPaths } from '../../../constants/routes';
import { selectChosenProfileData } from '../selectors';
import { TP, performancesAggregationTypes } from '../../../constants/consts';
import FbTabs from '../../../components/FbTabs';
import { getEditTitle, getFullProfileTitle, getRouteWithSlug } from '../../../utils/common';
import { PerformancesTab } from './tabs/PerformancesTab';
import { ProfileLayout } from './tabs/ProfileLayout';
import { Router } from '../../../src/i18n';
import { getList, getWholeList } from '../../App/actions';
import { PRODUCTIONS, FILTER_AGGREGATION_SEARCH, PROFILE_REPERTOIRE } from '../../../constants/endpointConstants';
import {
  getChosenProfileProductionsAction,
  getProfileProductionsSeasonsAction,
  getChosenProfileRepertoireAction,
} from '../actions';
import { defaultArtistProductionsPagination } from '../constants';
import RepertoireLayout from './tabs/RepertoireLayout';
import { selectPrivacyOptions } from '../../App/selectors';
import EditStubPageHeader from '../../../components/EditComponents/EditStubPageHeader';

export const artistEditTabValues = {
  personal: 'personal',
  schedule: 'schedule',
  repertoire: 'repertoire',
  agency: 'agency',
};

const editArtistTabs = {
  [artistEditTabValues.personal]: {
    value: artistEditTabValues.personal,
    name: `${TP}.FN_EDITARTIST_PROFILE_INFO`,
    subtitle: `${TP}.FN_EDITARTIST_PROFILE_INFO_DESCRIPTION`,
  },
  [artistEditTabValues.schedule]: {
    value: artistEditTabValues.schedule,
    name: `${TP}.FN_EDITARTIST_SCHEDULE`,
    subtitle: `${TP}.FN_EDITARTIST_SCHEDULE_DESCRIPTION`,
  },
  [artistEditTabValues.repertoire]: {
    value: artistEditTabValues.repertoire,
    name: `${TP}.m_ARTIST_REPERTOIRE`,
  },
};
const { profileAgg, seasonAgg, typeAgg } = performancesAggregationTypes;

export function ArtistEdit({ t, selectedTab, customBreadcrumbs }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const profile = useSelector(selectChosenProfileData());
  const privacyOptions = useSelector(selectPrivacyOptions());
  const NextRouter = useRouter();
  const [tab, selectTab] = React.useState(selectedTab || get(NextRouter, 'query.tab') || artistEditTabValues.personal);

  const setSelectedTab = value => {
    Router.push(artistsPaths.edit, `${getRouteWithSlug(route.ARTISTS, profile)}/edit?tab=${value}`, { shallow: true });
    selectTab(value);
    switch (value) {
      case artistEditTabValues.schedule:
        dispatch(
          getChosenProfileProductionsAction({
            profileId: profile?.id,
            params: {
              queryParams: {
                [profileAgg]: profile?.id,
                sort: '-year',
                show_hidden: true,
                as_edit: true,
              },
              pagination: defaultArtistProductionsPagination,
            },
          }),
        );
        dispatch(
          getList({
            endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
            sagaRoutine: getProfileProductionsSeasonsAction,
            params: {
              queryParams: {
                [typeAgg]: seasonAgg,
                [profileAgg]: profile?.id,
              },
            },
          }),
        );
        break;
      case artistEditTabValues.repertoire:
        dispatch(
          getWholeList({
            endpoint: PROFILE_REPERTOIRE(profile?.id),
            sagaRoutine: getChosenProfileRepertoireAction,
            params: { queryParams: { show_hidden: true } },
          }),
        );
        break;
      default:
        break;
    }
  };

  const profileTitle = getFullProfileTitle(profile);
  const title = getEditTitle(t, profileTitle);

  return (
    <Layout title={title} customBreadcrumbs={customBreadcrumbs}>
      <LayoutContent>
        <EditStubPageHeader
          entity={profile}
          backToLinkProps={{
            href: `${route.ARTISTS}/[id]/[[...action]]`,
            as: getRouteWithSlug(route.ARTISTS, profile),
          }}
          backToLinkText={`${TP}.FN_EDITARTIST_BACK_TO_ARTIST_PAGE`}
          entityInfo={
            <div className={classes.artistType}>
              {get(profile, 'profession.qualification.name') || get(profile, 'profession.name', '')}
            </div>
          }
          viewModeLinkProps={{
            href: `${route.ARTISTS}/[id]/[[...action]]`,
            as: getRouteWithSlug(route.ARTISTS, profile),
          }}
        />
        <FbTabs tabs={Object.values(editArtistTabs)} initialTabValue="seasons" value={tab} onChange={setSelectedTab} />
        <div className={classes.content}>
          {tab === artistEditTabValues.personal && (
            <ProfileLayout t={t} tab={editArtistTabs[artistEditTabValues.personal]} privacyOptions={privacyOptions} />
          )}
          {tab === artistEditTabValues.schedule && (
            <PerformancesTab
              t={t}
              tab={editArtistTabs[artistEditTabValues.schedule]}
              profile={profile}
              privacyOptions={privacyOptions}
            />
          )}
          {tab === artistEditTabValues.repertoire && (
            <RepertoireLayout t={t} tab={editArtistTabs[artistEditTabValues.repertoire]} />
          )}
          {tab === artistEditTabValues.agency && 'agency forms'}
        </div>
      </LayoutContent>
    </Layout>
  );
}

ArtistEdit.propTypes = {
  t: PropTypes.func.isRequired,
  customBreadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

export default ArtistEdit;
